import { Box, Button, styled, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import React, { useEffect, useState } from "react";
import {
	getCalendarEventsForInterviews,
	IInterviewCalendarEvent,
} from "../utilities/calendar";
import {
	InterviewFilter,
	InterviewSearch,
	InterviewTable,
} from "../interfaces/Interview";
import {
	GetCandidates,
	GetInterviews,
	GetManagers,
	GetStatuses,
} from "../requester/functions";
import getFilter from "../utilities/filter";
import {
	ArrayContainer,
	Filter,
	Pagination,
	ResponseContainer,
} from "../interfaces/RequestBase";
import UpdateInterviewModal from "../components/UpdateInterviewModal";
import FilterBase from "../components/Filter";
import { Status } from "../interfaces/Status";
import DataSelect from "../components/DataInputs/DataSelect";
import { ManagerSearch, ManagerTable } from "../interfaces/Manager";
import { CandidateSearch, CandidateTable } from "../interfaces/Candidate";
import DataTextField from "../components/DataInputs/DataTextField";
import { InfinityPagination } from "../hooks/Pagination";

const localizer = momentLocalizer(moment); // or globalizeLocalizer

interface IProps {}

const InterviewsPageBck = (props: IProps) => {
	const [interviews, setInterviews] = useState<InterviewTable[]>([]);

	const [status, setStatus] = useState<Status[]>([]);
	const [managers, setManagers] = useState<ManagerTable[]>([]);
	const [candidates, setCandidates] = useState<CandidateTable[]>([]);

	const [totalCount, setTotalCount] = useState<number>(0);
	const [filter, setFilter] = useState<InterviewFilter>({});
	const [search, setSearch] = useState<InterviewSearch>({});

	const [searchManager, setSearchManager] = useState<ManagerSearch>({});
	const [searchCandidate, setSearchCandidate] = useState<CandidateSearch>({});

	const [selectedInterview, setSelectedInterview] =
		useState<InterviewTable | null>();

	const [selectedDate, setSelectedDate] = useState<Date | undefined>(
		new Date()
	);
	const [events, setEvents] = useState<IInterviewCalendarEvent[]>([]);

	const setMonth = (delta: number) => {
		if (selectedDate) {
			const tmpDate = new Date(selectedDate);
			tmpDate.setMonth(tmpDate.getMonth() + delta);
			setSelectedDate(tmpDate);
		}
	};

	const setNextMonth = () => setMonth(1);
	const setPrevMonth = () => setMonth(-1);

	const handleDateChange = (date: Date | undefined) => setSelectedDate(date);

	const onSelectInterview = (event: IInterviewCalendarEvent) => {
		const index = interviews.findIndex((x) => x.id === event.id);
		if (index !== -1) {
			setSelectedInterview(interviews[index]);
		} else {
			setSelectedInterview(undefined);
		}
	};

	const eventPropGetter = (event: IInterviewCalendarEvent) => {
		if (!event.id) {
			if (moment(new Date()).endOf("day").isSameOrAfter(event.start)) {
				return { style: { backgroundColor: "red" } };
			}
		}

		return {};
	};

	useEffect(() => {
		setEvents(getCalendarEventsForInterviews(interviews));
	}, [interviews]);

	useEffect(() => {
		var req: Filter<InterviewFilter, InterviewSearch> = getFilter(
			InfinityPagination,
			filter,
			search
		);

		GetInterviews(
			req,
			(resp: ResponseContainer<ArrayContainer<InterviewTable>>) => {
				if (resp.status === "ok" && resp.data) {
					setInterviews(resp.data.data);
					setTotalCount(resp.data.total_count || 0);
				}
			}
		);
	}, [filter, search]);

	useEffect(() => {
		setFilter({
			...filter,
			period: {
				start_date: moment(selectedDate).startOf("month").valueOf(),
				end_date: moment(selectedDate).endOf("month").valueOf(),
			},
		});
	}, [selectedDate]);

	useEffect(() => {
		GetCandidates(
			{
				search_by: searchCandidate,
				pagination: { page: 0, size: 25 },
			},
			(resp: ResponseContainer<ArrayContainer<CandidateTable>>) => {
				if (resp.status === "ok" && resp.data) {
					setCandidates(resp.data.data);
				}
			}
		);
	}, [searchCandidate]);

	useEffect(() => {
		GetManagers(
			{
				search_by: searchManager,
				pagination: { page: 0, size: 25 },
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setManagers(resp.data.data);
				}
			}
		);
	}, [searchManager]);

	useEffect(() => {
		GetStatuses({ filter_by: { type: "interview" } }, (resp) => {
			if (resp.status === "ok" && resp.data) {
				setStatus(resp.data);
			}
		});
	}, []);

	const CalendarStyled = styled(Calendar)(({ theme }) =>
		theme.palette.mode === "dark"
			? {
					"& .rbc-off-range-bg": { backgroundColor: "#26292B" },
					"& .rbc-today": { backgroundColor: "#3a3e41" },
			  }
			: {}
	);

	return (
		<div className="flex flex-col">
			<div className="flex justify-between">
				<div className="flex gap-2">
					<Button
						type="submit"
						variant="contained"
						color="primary"
						onClick={setPrevMonth}
					>
						&lt;
					</Button>
					<LocalizationProvider
						dateAdapter={MomentUtils}
						adapterLocale={moment.locale("ru")}
					>
						<DatePicker
							openTo="month"
							views={["year", "month"]}
							label="Год и месяц"
							value={selectedDate}
							onChange={(date) => handleDateChange(date || undefined)}
							renderInput={(params) => <TextField {...params} />}
						/>
					</LocalizationProvider>
					<Button
						type="submit"
						variant="contained"
						color="primary"
						onClick={setNextMonth}
					>
						&gt;
					</Button>
				</div>
				<FilterBase setSearch={setSearch} setFilter={setFilter}>
					<DataTextField
						keyValue={"candidate_name"}
						value={search}
						setValue={setSearch}
						inputProps={{ label: "Поиск кандидата" }}
					/>
					<DataTextField
						keyValue={"manager_name"}
						value={search}
						setValue={setSearch}
						inputProps={{ label: "Поиск менеджера" }}
					/>
					<DataSelect
						keyValue={"id_status"}
						list={status}
						value={filter}
						setValue={setFilter}
						label="Статус"
					/>
				</FilterBase>
			</div>
			<Box m={1} />
			<CalendarStyled
				culture="ru-RU"
				localizer={localizer}
				events={events}
				defaultView="month"
				drilldownView="agenda"
				views={["month"]}
				date={selectedDate}
				onNavigate={() => {}}
				toolbar={false}
				popup={true}
				eventPropGetter={(event) =>
					eventPropGetter(event as IInterviewCalendarEvent)
				}
				onSelectEvent={(event) =>
					onSelectInterview(event as IInterviewCalendarEvent)
				}
				style={{ height: 500 }}
			/>
			<UpdateInterviewModal
				open={selectedInterview !== null}
				interview={selectedInterview || undefined}
				onClose={() => setSelectedInterview(null)}
				onUpdate={() => setSelectedInterview(null)}
			/>
		</div>
	);
};

export default InterviewsPageBck;
